import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import { useParams } from "react-router-dom";
import { integrationsApi } from "../../api/integrations";
import { projectsApi } from "../../api/projects";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import { isSuperAdmin } from "../../utils/roles";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import ModoIntegrationCard from "../../components/dashboard/payments/ModoIntegrationCard";
import { useProjectSettingsDirty } from "../../components/dashboard/ProjectSettingsUnsavedContext";

import EditorialIntro from "../../components/dashboard/EditorialIntro";
const BRAND = { orange: "#F35A3A", dark: "#19242F" };

const emptyIntegration = {
  enabled: true,
  connected: false,
  mode: "production",
  publicKey: "",
  currency: "ARS",
  locale: "es-AR",
  account: {},
  hasAccessToken: false,
  hasWebhookSecret: false,
  webhookUrl: "",
  lastTestedAt: null,
  lastError: "",
};

function formatDate(value) {
  if (!value) return "Todavía no se probó";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export default function ProjectEcommercePage({ embedded = false }) {
  const isMobileViewport = useMediaQuery("(max-width:899.95px)", { noSsr: true });
  const compactMobile = embedded && isMobileViewport;
  const { projectId } = useParams();
  const { user } = useAuth();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [member, setMember] = React.useState(null);
  const [integration, setIntegration] = React.useState(emptyIntegration);
  const [form, setForm] = React.useState({
    enabled: true,
    mode: "production",
    publicKey: "",
    accessToken: "",
    webhookSecret: "",
    currency: "ARS",
    locale: "es-AR",
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId) || activeProject,
    [projects, projectId, activeProject]
  );

  const canManage = isSuperAdmin(user) || ["owner", "admin"].includes(member?.role);
  const ecommerceEnabled = isProjectModuleEnabled(project || contextProject, "ecommerce");
  const mercadoPagoDirty = React.useMemo(() => {
    if (loading || !ecommerceEnabled) return false;
    return Boolean(
      form.enabled !== (integration.enabled !== false)
      || String(form.mode || "production") !== String(integration.mode || "production")
      || String(form.publicKey || "").trim() !== String(integration.publicKey || "").trim()
      || String(form.currency || "ARS").trim().toUpperCase() !== String(integration.currency || "ARS").trim().toUpperCase()
      || String(form.locale || "es-AR").trim() !== String(integration.locale || "es-AR").trim()
      || String(form.accessToken || "").trim()
      || String(form.webhookSecret || "").trim()
    );
  }, [loading, ecommerceEnabled, form, integration]);
  useProjectSettingsDirty("ecommerce-mercado-pago", mercadoPagoDirty);

  const applyIntegration = React.useCallback((nextIntegration = {}) => {
    const next = { ...emptyIntegration, ...nextIntegration };
    setIntegration(next);
    setForm((previous) => ({
      ...previous,
      enabled: next.enabled !== false,
      mode: next.mode || "production",
      publicKey: next.publicKey || "",
      accessToken: "",
      webhookSecret: "",
      currency: next.currency || "ARS",
      locale: next.locale || "es-AR",
    }));
  }, []);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const projectData = await projectsApi.get(projectId);
      setProject(projectData.project || null);
      setMember(projectData.member || null);

      if (projectData.project) {
        setActiveProject(projectData.project);
      }

      if (isProjectModuleEnabled(projectData.project, "ecommerce")) {
        const integrationData = await integrationsApi.getMercadoPago(projectId);
        applyIntegration(integrationData.integration || emptyIntegration);
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar la configuración de e-commerce");
    } finally {
      setLoading(false);
    }
  }, [projectId, setActiveProject, applyIntegration]);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    load();
  }, [load]);

  const change = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!canManage) return;

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const payload = {
        enabled: form.enabled,
        mode: form.mode,
        publicKey: form.publicKey.trim(),
        currency: form.currency.trim().toUpperCase(),
        locale: form.locale.trim(),
        testConnection: true,
      };
      if (form.accessToken.trim()) payload.accessToken = form.accessToken.trim();
      if (form.webhookSecret.trim()) payload.webhookSecret = form.webhookSecret.trim();

      const data = await integrationsApi.saveMercadoPago(projectId, payload);
      applyIntegration(data.integration || emptyIntegration);
      setMessage(data.message || "Mercado Pago quedó guardado y verificado.");
    } catch (err) {
      if (err?.data?.integration) applyIntegration(err.data.integration);
      setError(err.message || "No se pudo guardar Mercado Pago");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    try {
      setTesting(true);
      setError("");
      setMessage("");
      const data = await integrationsApi.testMercadoPago(projectId);
      applyIntegration(data.integration || emptyIntegration);
      setMessage(data.message || "Conexión verificada.");
    } catch (err) {
      if (err?.data?.integration) applyIntegration(err.data.integration);
      setError(err.message || "No se pudo verificar la conexión");
    } finally {
      setTesting(false);
    }
  };

  const disconnect = async () => {
    const confirmed = window.confirm(
      "¿Desconectar Mercado Pago? Se borrarán las credenciales guardadas para este proyecto."
    );
    if (!confirmed) return;

    try {
      setDisconnecting(true);
      setError("");
      setMessage("");
      const data = await integrationsApi.disconnectMercadoPago(projectId);
      applyIntegration(data.integration || emptyIntegration);
      setMessage(data.message || "Mercado Pago fue desconectado.");
    } catch (err) {
      setError(err.message || "No se pudo desconectar Mercado Pago");
    } finally {
      setDisconnecting(false);
    }
  };

  const copyWebhook = async () => {
    if (!integration.webhookUrl) return;
    try {
      await navigator.clipboard.writeText(integration.webhookUrl);
      setMessage("Webhook copiado al portapapeles.");
    } catch (_error) {
      window.prompt("Copiá esta URL de webhook", integration.webhookUrl);
    }
  };

  if (loading) {
    return <DashboardLoadingState label="Cargando cobros online…" minHeight={360} />;
  }

  if (!ecommerceEnabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="E-commerce"
        description="Este nivel agrega checkout y medios de pago online. Primero deben estar habilitados Productos y Pedidos."
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
        <Box sx={{ display: compactMobile ? "none" : "block", flex: 1, minWidth: 0 }}>
          <EditorialIntro
            title="Tienda y pagos"
            titleVariant={embedded ? "h5" : "h3"}
            context={`${project?.name || projectId} · cobros online y checkout.`}
            helpTitle="Cómo se conectan los medios de pago"
            helpText="Cada proveedor se conecta con las credenciales propias del comercio y mantiene un estado independiente por proyecto. Podés usar Mercado Pago, configurar MODO o sumar otros proveedores sin mezclar sus credenciales."
            compact
          />
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            icon={<PaymentsRoundedIcon />}
            label={integration.connected ? "Mercado Pago conectado" : "Mercado Pago sin conectar"}
            color={integration.connected ? "success" : "default"}
            sx={{ fontWeight: 850 }}
          />
          <Chip
            label={form.mode === "test" ? "Mercado Pago · prueba" : "Mercado Pago · producción"}
            color={form.mode === "test" ? "warning" : "primary"}
            variant="outlined"
            sx={{ fontWeight: 850 }}
          />
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}
      {!canManage && (
        <Alert severity="warning">
          Sólo el owner o un administrador del proyecto puede modificar credenciales de cobros online.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Box sx={{ width: 46, height: 46, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
                  <KeyRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                    Cuenta de Mercado Pago
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>
                    Cada cliente conecta su propia aplicación y cobra directamente en su cuenta.
                  </Typography>
                </Box>
              </Stack>

              <Box component="form" onSubmit={save}>
                <Stack spacing={2.25}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2, borderRadius: 3, bgcolor: "#F6F7FB" }}>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>Habilitar Mercado Pago</Typography>
                      <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.6)" }}>
                        Este medio sólo queda disponible cuando su conexión está validada.
                      </Typography>
                    </Box>
                    <Switch checked={form.enabled} onChange={(event) => change("enabled", event.target.checked)} disabled={!canManage} />
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField select label="Entorno" value={form.mode} onChange={(event) => change("mode", event.target.value)} disabled={!canManage} fullWidth>
                        <MenuItem value="test">Prueba</MenuItem>
                        <MenuItem value="production">Producción</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField label="Moneda" value={form.currency} onChange={(event) => change("currency", event.target.value)} disabled={!canManage} inputProps={{ maxLength: 3 }} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField label="Locale" value={form.locale} onChange={(event) => change("locale", event.target.value)} disabled={!canManage} fullWidth />
                    </Grid>
                  </Grid>

                  <TextField
                    label="Public key"
                    value={form.publicKey}
                    onChange={(event) => change("publicKey", event.target.value)}
                    disabled={!canManage}
                    placeholder="APP_USR-..."
                    helperText="Se envía de forma segura al storefront para inicializar el Payment Brick."
                    fullWidth
                  />
                  <TextField
                    label="Access token"
                    value={form.accessToken}
                    onChange={(event) => change("accessToken", event.target.value)}
                    disabled={!canManage}
                    type="password"
                    placeholder={integration.hasAccessToken ? "Ya existe uno guardado; dejalo vacío para conservarlo" : "APP_USR-..."}
                    helperText={integration.hasAccessToken ? "El token actual está cifrado y nunca vuelve al navegador." : "Se cifra en el backend antes de guardarse."}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <VisibilityOffRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Secret del webhook"
                    value={form.webhookSecret}
                    onChange={(event) => change("webhookSecret", event.target.value)}
                    disabled={!canManage}
                    type="password"
                    placeholder={integration.hasWebhookSecret ? "Ya existe uno guardado; dejalo vacío para conservarlo" : "Firma secreta de Webhooks"}
                    helperText="Mercado Pago lo muestra al configurar la firma de notificaciones. El backend lo usa para validar x-signature."
                    fullWidth
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                    <Button
                      type="submit"
                      disabled={!canManage || saving}
                      startIcon={saving ? <CircularProgress size={18} /> : <SaveRoundedIcon />}
                      variant="contained"
                      sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, px: 3 }}
                    >
                      {saving ? "Guardando y probando..." : "Guardar y probar conexión"}
                    </Button>
                    <Button
                      type="button"
                      disabled={!canManage || testing || !integration.hasAccessToken}
                      startIcon={testing ? <CircularProgress size={18} /> : <RefreshRoundedIcon />}
                      onClick={testConnection}
                      variant="outlined"
                      sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                    >
                      Probar de nuevo
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack spacing={3}>
            <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(25,36,47,0.06)", color: BRAND.dark }}>
                    <LinkRoundedIcon />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                      Webhook del proyecto
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>
                      Es único para {project?.name || projectId}.
                    </Typography>
                  </Box>
                </Stack>

                <TextField
                  value={integration.webhookUrl || "Se generará desde el backend"}
                  fullWidth
                  multiline
                  minRows={2}
                  InputProps={{
                    readOnly: true,
                    endAdornment: integration.webhookUrl ? (
                      <InputAdornment position="end">
                        <Tooltip title="Copiar webhook">
                          <IconButton onClick={copyWebhook} edge="end">
                            <ContentCopyRoundedIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                  }}
                />

                <Alert severity="info" sx={{ mt: 2 }}>
                  Pegá esta URL en Webhooks de Mercado Pago y activá el evento <strong>Pagos</strong>.
                </Alert>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                  Estado de la integración
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: "rgba(25,36,47,0.62)" }}>Cuenta</Typography>
                    <Typography sx={{ fontWeight: 850, textAlign: "right" }}>
                      {integration.account?.email || integration.account?.nickname || "Sin validar"}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: "rgba(25,36,47,0.62)" }}>Última prueba</Typography>
                    <Typography sx={{ fontWeight: 850, textAlign: "right" }}>{formatDate(integration.lastTestedAt)}</Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: "rgba(25,36,47,0.62)" }}>Access token</Typography>
                    <Chip size="small" label={integration.hasAccessToken ? "Configurado" : "Pendiente"} color={integration.hasAccessToken ? "success" : "default"} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography sx={{ color: "rgba(25,36,47,0.62)" }}>Firma webhook</Typography>
                    <Chip size="small" label={integration.hasWebhookSecret ? "Configurada" : "Pendiente"} color={integration.hasWebhookSecret ? "success" : "warning"} />
                  </Stack>
                  {integration.lastError && <Alert severity="error">{integration.lastError}</Alert>}
                </Stack>

                <Accordion
                  elevation={0}
                  disableGutters
                  sx={{ mt: 2, border: "1px solid rgba(25,36,47,.08)", borderRadius: "12px !important", overflow: "hidden", "&:before": { display: "none" } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: .75 } }}>
                    <Typography variant="body2" sx={{ fontWeight: 900, color: BRAND.dark }}>Opciones avanzadas</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Typography variant="caption" sx={{ display: "block", mb: 1, color: "rgba(25,36,47,.58)" }}>
                      Desconectar elimina la credencial guardada y deshabilita los cobros con este proveedor hasta volver a configurarlo.
                    </Typography>
                    <Button
                      color="error"
                      variant="text"
                      startIcon={<DeleteOutlineRoundedIcon />}
                      disabled={!canManage || disconnecting || (!integration.hasAccessToken && !integration.publicKey)}
                      onClick={disconnect}
                      sx={{ textTransform: "none", fontWeight: 900 }}
                    >
                      {disconnecting ? "Desconectando..." : "Desconectar cuenta"}
                    </Button>
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <ModoIntegrationCard
        projectId={projectId}
        projectName={project?.name || projectId}
        canManage={canManage}
      />
    </Stack>
  );
}
